import React, { useCallback, useEffect, useState } from 'react';
import { Activity, RefreshCw, CheckCircle2, XCircle, AlertTriangle, Copy } from 'lucide-react';
import { getQueueSize, getTrackerState, flushGpsQueue } from '../../lib/gpsTracker';
import { getBatteryOptStatus } from '../../lib/batteryOptStatus';
import { APP_VERSION_LABEL } from '../../version';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Feb 22, 2026 — Staff-side self-diagnostic screen.
//
// Motivation: when staff calls to say "GPS not working" the manager has no
// way to know if the app is running, if pings are queued, if battery
// optimisation is on, if the server received the last ping, etc. This
// screen turns every "app broken" support call into a single screenshot
// with structured data — reducing back-and-forth from 5 min → 30 sec.
//
// All checks run locally on device (no server round-trips except a probe
// GET to /api/attendance/today to confirm connectivity + auth token).

async function _serverProbe() {
  const t0 = performance.now();
  try {
    const token = localStorage.getItem('token');
    const r = await fetch(`${API_URL}/api/attendance/today`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return { ok: r.ok, status: r.status, ms: Math.round(performance.now() - t0) };
  } catch (e) {
    return { ok: false, status: 0, err: e?.message || String(e), ms: Math.round(performance.now() - t0) };
  }
}

async function _geoProbe() {
  if (!navigator.geolocation) return { ok: false, err: 'Geolocation API missing' };
  return new Promise((resolve) => {
    const t0 = performance.now();
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        ok: true, lat: pos.coords.latitude, lng: pos.coords.longitude,
        accuracy_m: Math.round(pos.coords.accuracy),
        ms: Math.round(performance.now() - t0),
      }),
      (err) => resolve({ ok: false, err: err?.message || 'unknown', code: err?.code, ms: Math.round(performance.now() - t0) }),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
    );
  });
}

function Row({ label, ok, value, hint }) {
  const Icon = ok === true ? CheckCircle2 : ok === false ? XCircle : AlertTriangle;
  const color = ok === true ? 'text-emerald-600' : ok === false ? 'text-red-600' : 'text-amber-600';
  return (
    <div className="flex items-start gap-2 py-2 border-b border-gray-100 last:border-b-0" data-testid={`gps-diag-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <Icon size={16} className={`mt-0.5 shrink-0 ${color}`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900">{label}</div>
        <div className="text-xs text-gray-600 break-words">{value}</div>
        {hint && <div className="text-[11px] text-gray-500 mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}

export default function GpsDiagnostic({ open, onClose }) {
  const [running, setRunning] = useState(false);
  const [snap, setSnap] = useState(null);

  const run = useCallback(async () => {
    setRunning(true);
    const now = new Date();
    const state = getTrackerState();
    const q = await getQueueSize().catch(() => -1);
    const batt = await getBatteryOptStatus().catch(() => ({ native: false }));
    const geo = await _geoProbe();
    const srv = await _serverProbe();
    const online = navigator.onLine;
    const conn = navigator.connection || {};
    const ua = navigator.userAgent || '';
    const androidVer = /Android\s+(\d+)/.exec(ua)?.[1] || null;

    setSnap({
      generated_at: now.toISOString(),
      tracker: state,
      queue_size: q,
      battery_opt: batt,
      geolocation: geo,
      server_probe: srv,
      network: {
        online,
        effective_type: conn.effectiveType || '',
        downlink_mbps: conn.downlink || null,
        rtt_ms: conn.rtt || null,
        save_data: !!conn.saveData,
      },
      device: { user_agent: ua, android: androidVer, platform: navigator.platform || '' },
      version: APP_VERSION_LABEL || 'unknown',
    });
    setRunning(false);
  }, []);

  useEffect(() => { if (open) run(); }, [open, run]);

  // Escape-key close — standard modal keyboard contract.
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onClose]);

  const copyText = useCallback(async () => {
    if (!snap) return;
    try { await navigator.clipboard.writeText(JSON.stringify(snap, null, 2)); } catch (_) { /* ignore */ }
  }, [snap]);

  const flushNow = useCallback(async () => {
    setRunning(true);
    try { await flushGpsQueue(); } catch (_) { /* ignore */ }
    await run();
  }, [run]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="gps-diagnostic">
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl overflow-hidden shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-blue-50">
          <div className="flex items-center gap-2">
            <Activity size={18} className="text-blue-700" />
            <h2 className="font-bold text-gray-900">GPS Diagnostic</h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900 text-2xl leading-none" data-testid="gps-diag-close" aria-label="Close">×</button>
        </div>

        <div className="p-4 space-y-1 overflow-y-auto flex-1">
          {!snap && running && <div className="text-center py-8 text-gray-500 text-sm">Running checks…</div>}
          {snap && (
            <>
              <Row
                label="Tracker running"
                ok={snap.tracker.active}
                value={snap.tracker.active ? `Yes · ${snap.tracker.mode} mode` : 'No — you may not be checked in'}
              />
              <Row
                label="Server reachable"
                ok={snap.server_probe.ok}
                value={snap.server_probe.ok
                  ? `Yes · HTTP ${snap.server_probe.status} · ${snap.server_probe.ms}ms`
                  : `Failed · ${snap.server_probe.err || `HTTP ${snap.server_probe.status}`}`}
              />
              <Row
                label="GPS chip"
                ok={snap.geolocation.ok}
                value={snap.geolocation.ok
                  ? `${snap.geolocation.lat.toFixed(5)}, ${snap.geolocation.lng.toFixed(5)} · ±${snap.geolocation.accuracy_m}m · ${snap.geolocation.ms}ms`
                  : `Failed · ${snap.geolocation.err} (code ${snap.geolocation.code ?? '?'})`}
                hint={!snap.geolocation.ok ? 'Enable Location in phone settings, or grant "Allow all the time" permission.' : null}
              />
              <Row
                label="Battery optimisation"
                ok={snap.battery_opt.ignored === true ? true : (snap.battery_opt.ignored === false ? false : null)}
                value={snap.battery_opt.native
                  ? (snap.battery_opt.ignored ? 'Whitelisted (good)' : 'Still ON — will kill background GPS')
                  : 'Not applicable (web / iOS)'}
                hint={snap.battery_opt.ignored === false ? 'Tap the battery icon on dashboard to run the wizard.' : null}
              />
              <Row
                label="Pending pings in queue"
                ok={snap.queue_size === 0 ? true : (snap.queue_size < 20 ? null : false)}
                value={`${snap.queue_size} points queued`}
                hint={snap.queue_size > 0 ? 'Queue drains automatically when online. Tap "Flush now" below if stuck.' : null}
              />
              <Row
                label="Network"
                ok={snap.network.online}
                value={snap.network.online
                  ? `Online · ${snap.network.effective_type || '?'} · ${snap.network.downlink_mbps ?? '?'} Mbps · RTT ${snap.network.rtt_ms ?? '?'}ms${snap.network.save_data ? ' · Data Saver ON' : ''}`
                  : 'Offline'}
                hint={snap.network.save_data ? 'Data Saver is ON — may throttle GPS pings.' : null}
              />
              <Row
                label="Device"
                ok={null}
                value={`${snap.battery_opt.manufacturer || 'Device'} ${snap.battery_opt.model || ''} · Android ${snap.device.android || '?'} · SDK ${snap.battery_opt.sdk || '?'}`}
              />
              <Row
                label="App version"
                ok={null}
                value={snap.version}
              />
            </>
          )}
        </div>

        <div className="border-t p-3 flex flex-wrap gap-2 bg-gray-50">
          <button
            onClick={run}
            disabled={running}
            className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold"
            data-testid="gps-diag-rerun"
          >
            <RefreshCw size={13} className={running ? 'animate-spin' : ''} /> Re-run
          </button>
          <button
            onClick={flushNow}
            disabled={running || (snap && snap.queue_size === 0)}
            className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-1 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold"
            data-testid="gps-diag-flush"
          >
            Flush queue
          </button>
          <button
            onClick={copyText}
            disabled={!snap}
            className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-1 px-3 py-2 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-gray-800 rounded-lg text-sm font-semibold"
            data-testid="gps-diag-copy"
          >
            <Copy size={13} /> Copy report
          </button>
        </div>
      </div>
    </div>
  );
}
